import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanCurrency } from 'store/useBusinessToolStore/selectors';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { AlertComponent } from 'components/_basic/AlertComponent';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import { formatNumber } from 'helpers/numbers';
import { concatWithDot } from 'helpers/string';

import { IFundingDirectionsContainer } from './types';
import { EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES, prefix } from '../../utils/form';
import { FundingDirections } from '../FundingDirections';

import styles from './FundingDirections.module.scss';

const totalCostFieldName = concatWithDot(globalPrefix, `${GLOBAL_FIELD_NAMES.TOTAL_COST}`);
const totalCostWatchFieldNames = [
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.RESEARCH_COST,
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.MARKETING_COST,
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.WORKING_CAPITAL_COST,
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.EQUIPMENT_COST,
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.CONSTRUCTION_COST,
  EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.OTHER_COST,
];

export const FundingDirectionsContainer: React.FC<IFundingDirectionsContainer> = ({ disabled }) => {
  const { t } = useTranslation();

  const { setFocus, setValue, getValues } = useFormContext();

  const [isTotalCostMatch, setIsTotalCostMatch] = useState(true);
  const currency = useBusinessToolStore(getBusinessPlanCurrency);
  const currencySymbol = getCurrencySymbol(currency);

  const watchCostFields = useWatch({
    name: totalCostWatchFieldNames.map((fieldName) => concatWithDot(prefix, `${fieldName}.currencyAmount`)),
  });

  const totalCostField = +getValues(totalCostFieldName);
  const totalCost = watchCostFields.reduce((acc, item) => (Number(item) ? acc + Number(item) : acc), 0);

  const totalCostOfYourProject = useWatch({
    name: [totalCostFieldName],
  }).reduce((acc, item) => acc + Number(item), 0);

  const handleSavePercent = (values: string[]) => {
    totalCostWatchFieldNames.forEach((fieldName, index) => {
      const percentField = `${concatWithDot(prefix, fieldName)}.percentage`;
      const percent = Number(values[index]) ? (Number(values[index]) / totalCost) * 100 : 0;

      setValue(percentField, percent);
    });
  };

  useDebouncedSave({ data: watchCostFields, saveFunction: handleSavePercent, debounceTime: 500 });

  useEffect(() => {
    setIsTotalCostMatch(totalCost === totalCostOfYourProject);
  }, [totalCost, totalCostOfYourProject]);

  const setTotalFocus = () => {
    setFocus(concatWithDot(totalCostFieldName));
  };

  return (
    <Box>
      <FundingDirections totalCost={totalCost} isTotalCostMatch={isTotalCostMatch} disabled={disabled} />
      <Box className={styles.totalContainer} onClick={setTotalFocus}>
        <Box className={styles.totalText}>{t('executiveSummary.financialNeeds.totalCost')}: </Box>
        {currencySymbol}
        {formatNumber(totalCostField || totalCost)}
      </Box>
      {!isTotalCostMatch && (
        <AlertComponent severity="error" className={styles.alert}>
          <Typography variant="subtitle2">
            <Trans i18nKey="totalShouldMatch_plural">
              <Box component="span" className={styles.totalText}>
                {t('total')}
                &nbsp;
              </Box>
              {t('shouldMatch')}
              &nbsp;
              <Box className={clsx(styles.totalText, styles.totalLink)} onClick={setTotalFocus}>
                {t('executiveSummary.financialNeeds.totalCost')}
              </Box>
              {'.'}
              &nbsp;
              {t('correctToHaveDataVisualization')}
            </Trans>
          </Typography>
        </AlertComponent>
      )}
    </Box>
  );
};
